import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { pageBlockedFor } from "@/lib/roles";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Machines from "@/pages/Machines";
import MachineDetails from "@/pages/MachineDetails";
import Hires from "@/pages/Hires";
import StartHire from "@/pages/StartHire";
import Transport from "@/pages/Transport";
import Quotes from "@/pages/Quotes";
import Customers from "@/pages/Customers";
import NewCustomer from "@/pages/NewCustomer";
import MonthEnd from "@/pages/MonthEnd";
import InvoicesMoneyOwed from "@/pages/InvoicesMoneyOwed";
import DryRun from "@/pages/DryRun";
import RegoInspections from "@/pages/RegoInspections";
import Reports from "@/pages/Reports";
import Emails from "@/pages/Emails";
import Settings from "@/pages/Settings";
import Connections from "@/pages/Connections";
import TemplatesEditor from "@/pages/TemplatesEditor";
import SetPassword from "@/pages/SetPassword";
import BulkImport from "@/pages/BulkImport";
import BulkImportHires from "@/pages/BulkImportHires";
import TVMode from "@/pages/TVMode";

/* Some pages are Director-only because their page-load fetches are. Hiding them
   from the sidebar stops the nav route in, but a bookmarked or typed URL would
   still mount the page and throw on the first 403. Render a plain panel
   instead, so the app degrades rather than showing a blank screen. */
function RoleGuard({ path, children }) {
  const { user } = useAuth();
  if (pageBlockedFor(path, user)) {
    return (
      <div style={{padding: 40, color: "var(--ach-text-2)"}} data-testid="role-blocked">
        <h2 style={{margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: "var(--ach-text)"}}>
          Nothing here for your role
        </h2>
        <p style={{margin: 0}}>
          Settings is only available to a Director. If you need something changed here,
          ask one of them.
        </p>
      </div>
    );
  }
  return children;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div style={{padding: 40, color: "var(--ach-text-2)"}}>Signing you in…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  // Force a first-login password change before anything else is visible.
  if (user.must_change_password && loc.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<Protected><SetPassword /></Protected>} />
            {/* Kiosk / wall-TV view — deliberately OUTSIDE the Layout wrapper (no sidebar,
                no header). Still Protected so the session/token filtering applies. */}
            <Route path="/tv" element={<Protected><TVMode /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/machines" element={<Machines />} />
              <Route path="/machine-details" element={<MachineDetails />} />
              <Route path="/hires" element={<Hires />} />
              <Route path="/start-a-hire" element={<StartHire />} />
              <Route path="/transport" element={<Transport />} />
              <Route path="/quotes" element={<Quotes />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/new-customer" element={<NewCustomer />} />
              <Route path="/month-end" element={<MonthEnd />} />
              <Route path="/invoices-money-owed" element={<InvoicesMoneyOwed />} />
              <Route path="/dry-run" element={<DryRun />} />
              <Route path="/rego-inspections" element={<RegoInspections />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/emails" element={<Emails />} />
              <Route path="/settings" element={<RoleGuard path="/settings"><Settings /></RoleGuard>} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/settings/templates" element={<RoleGuard path="/settings"><TemplatesEditor /></RoleGuard>} />
              <Route path="/settings/bulk-import" element={<RoleGuard path="/settings"><BulkImport /></RoleGuard>} />
              <Route path="/settings/bulk-import-hires" element={<RoleGuard path="/settings"><BulkImportHires /></RoleGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
